import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonPage,
  IonTitle,
  IonToolbar,
  useIonViewDidLeave,
  useIonViewWillEnter,
} from '@ionic/react'
import React, { BaseSyntheticEvent, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { chevronBackOutline } from 'ionicons/icons'
import PCRHeader from '../../../../components/pcrHeader/PCRHeader.component'
import { getDrainSelector } from '../selectors/drain.selector'
import { IDrainState } from '../store/drain.types'
import Procedure from '../../../../components/procedure/Procedure.component'
import { TabConstants } from '../../../../components/tabs/Tabs.constants'
import { setDeleteProcedure, setProcedureLoaderItem } from '../../../timeline/store/timeline.actions'
import { filterOutObjectKeys, compareObjectsIfEqual } from '../../../../utilities/object.util'
import { showAlert } from '../../../vitals/store/vitals.actions'
import { initialDrainState } from '../store/drain.reducers'
import { IOption } from '../../../../common/common.types'
import { getCurrentDate, getCurrentTime, randomNumber } from '../../../../utilities'
import { DrainActionIds, DrainConstants } from '../store/drain.constants'
import { ProcedureConstants } from '../../../../components/procedure/store/procedure.constants'
import {
  resetToInitialState,
  saveDrain,
  setDepth,
  setDrainDefaults,
  setSide,
  setUpdateInputRows,
} from '../store/drain.actions'
import { setInputRows, setIsFirstVisit } from '../../../../components/procedure/store/procedure.actions'
import {
  setAction,
  setProcedure,
  setChestSuction,
  setDrainSuction,
  setFoleyColor,
  setCsfColor,
  setSize,
  setSizeUnit,
  setLocation,
  setIntercostalSpace,
} from '../store/drain.actions'
import { getDrainOptionsSelector } from '../../../options/selectors'
import { getAgeRangeGroupId, getDefaultsForAgeRange } from '../../../../utilities/procedures.util'
import { getDefaultsSelector } from '../../../defaults/selectors/defaults.selector'
import { getPatientDemographicsSelector } from '../../../patientDemographics/selectors'
import { getProcedureStateSelector } from '../../../../components/procedure/selectors'
import { goBack } from '../../../../utilities/navigation.util'
import { IServiceConfig } from '../../../configurations/store/configurations.types'
import { getServiceConfigSelector } from '../../../configurations/selectors/serviceConfig.selector'
import { IAlertState } from '../../../../components/alert/store/alert.types'
import { TimelineConstants } from '../../../timeline/store/timeline.constants'
import { IIntercostalSpaceOption } from '../../../options/store/options.types'

const Drain: React.FC = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const history = useHistory()
  const drain: IDrainState = useSelector(getDrainSelector)
  const {
    date,
    time,
    assessedBy,
    rolePerforming,
    rolePerformingPNNV,
    procedureComplications,
    response,
    responsePNNV,
    authorization,
    comments,
    successfulAttempt,
    pta,
    attempts,
    updateInputRows,
    size,
    chestSuction,
    action,
    csfColor,
    depth,
    drainSuction,
    foleyColor,
    side,
    location,
    intercostalSpace,
    sizeUnit,
    procedure,
    chestSuctionHiddenFlag,
    csfColorHiddenFlag,
    depthHiddenFlag,
    drainSuctionHiddenFlag,
    foleyColorHiddenFlag,
    procedureHiddenFlag,
    sideHiddenFlag,
    sizeUnitHiddenFlag,
    sizeHiddenFlag,
    isEkgImport,
    drainDefaults,
    procedurePerformedByAnotherUnit,
    procedurePerformedByAnotherUnitPNNV,
  } = drain

  const drainOptions = useSelector(getDrainOptionsSelector)
  const {
    actionOptions,
    procedureOptions,
    chestSuctionOptions,
    csfColorOptions,
    drainSuctionOptions,
    foleyColorOptions,
    sideOptions,
    sizeUnitOptions,
    locationOptions,
    intercostalSpaceOptions,
  } = drainOptions

  const { defaults } = useSelector(getDefaultsSelector)
  const { dOB, age, ageUnit } = useSelector(getPatientDemographicsSelector)
  const { isFirstVisit } = useSelector(getProcedureStateSelector)
  const serviceConfigData: IServiceConfig = useSelector(getServiceConfigSelector)
  const { DISPLAY_SECONDS } = serviceConfigData
  const displaySeconds = !!DISPLAY_SECONDS

  const dateTimeValidationCheck = () => {
    if (!date || !time) {
      dispatch(showAlert({ header: '', message: 'Date / Time is required' }))
      return false
    }
    return true
  }

  const initialStateCheck = () => {
    if (pta && ((date && time) || (!date && !time))) return true
    const fieldsToFilter = ['key', 'addToTimeline', 'updateInputRows', 'action', 'sizeUnit', 'drainDefaults']
    const currentState = filterOutObjectKeys(drain, fieldsToFilter)
    const initalState = filterOutObjectKeys(initialDrainState, fieldsToFilter)
    if (!compareObjectsIfEqual(currentState, initalState)) {
      return dateTimeValidationCheck()
    }
    return true
  }

  const save = () => {
    dispatch(saveDrain())
  }

  const onSaveClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (!pta && (!date || !time)) {
      dateTimeValidationCheck()
      return
    }
    const dateTimeValidationCheckResult = initialStateCheck()
    if (dateTimeValidationCheckResult) {
      history.push(TabConstants.TIMELINE_TAB)
    }
  }

  const onBackClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if ((!date || !time) && !pta) {
      const alert: IAlertState = {
        message: t('common.saveTimelineEntry'),
        buttons: [
          {
            text: t('common.buttons.Yes'),
            handler: () => {
              const date = getCurrentDate()
              let time = getCurrentTime()
              if (!displaySeconds) {
                time = time.replace(/(:\d+)(:\d+)$/, '$1:00')
              }

              dispatch({ type: 'drain/SET_TIME', time: time })
              dispatch({ type: 'drain/SET_DATE', date: date })
              save()
              goBack()
            },
          },
          {
            text: t('common.buttons.No'),
            role: 'destructive',
            handler: () => {
              const itemToRemove = {
                key: drain.key,
                rowActionType: TimelineConstants.SET_DRAIN_PROCEDURE_ITEM,
              }
              dispatch(setDeleteProcedure(true))
              dispatch({ type: TimelineConstants.DELETE_TIMELINE_ITEM, payload: itemToRemove })
              goBack()
            },
          },
        ],
      }
      dispatch(showAlert(alert))
      dispatch(setProcedureLoaderItem(false))
    } else {
      const dateTimeValidationCheck = initialStateCheck()
      if (dateTimeValidationCheck) {
        goBack()
      }
      dispatch(setProcedureLoaderItem(false))
    }
  }

  const actionOnChange = (option: IOption) => {
    if (action?.id === option?.id) return

    dispatch(resetToInitialState())
    dispatch(setAction(option))
  }

  const procedureOnChange = (option: IOption) => {
    dispatch(setProcedure(option))
    save()
  }

  const sizeOnChange = (values: string[]) => {
    dispatch(setSize(values.join('')))
    save()
  }

  const drainSuctionOnChange = (option: IOption) => {
    dispatch(setDrainSuction(option))
  }

  const chestSuctionOnChange = (option: IOption) => {
    dispatch(setChestSuction(option))
  }

  const foleyColorOnChange = (option: IOption) => {
    dispatch(setFoleyColor(option))
  }

  const csfColorOnChange = (option: IOption) => {
    dispatch(setCsfColor(option))
  }

  const sideOnChange = (option: IOption) => {
    dispatch(setSide(option))
    save()
  }

  const depthOnChange = (values: string[]) => {
    dispatch(setDepth(values.join('')))
    save()
  }

  const sizeUnitOnChange = (option: IOption) => {
    dispatch(setSizeUnit(option))
    save()
  }

  const locationOnChange = (option: IOption) => {
    dispatch(setLocation(option))
  }

  const intercostalSpaceOnChange = (option: IIntercostalSpaceOption) => {
    dispatch(setIntercostalSpace(option))
    save()
  }

  const actionId = String(action?.id)

  const isNeedleThoracostomy = actionId === DrainActionIds.NEEDLE_THORACOSTOMY

  const isSimpleFingerThoracostomy = actionId === DrainActionIds.SIMPLE_FINGER_THORACOSTOMY

  const filteredIntercostalSpaceOptions = intercostalSpaceOptions.filter((option) => {
    if (isNeedleThoracostomy) return option.isNeedleThoracostomy === 1
    if (isSimpleFingerThoracostomy) return option.isSimpleThoracostomy === 1
    return true
  })

  const inputRows = [
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: DrainConstants.SET_ACTION,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: false,
          props: {
            label: t('drain.action'),
            dataTestId: 'action',
            options: actionOptions,
            action: actionOnChange,
            listHeader: t('drain.action'),
            selectedOption: action,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: DrainConstants.SET_PROCEDURE,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: procedureHiddenFlag,
          props: {
            label: t('drain.procedure'),
            dataTestId: 'procedure',
            options: procedureOptions,
            action: procedureOnChange,
            listHeader: t('drain.procedure'),
            selectedOption: procedure,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: DrainConstants.SET_SIZE,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: sizeHiddenFlag,
          props: {
            label: isNeedleThoracostomy ? t('drain.gauge') : t('drain.size'),
            dataTestId: 'size',
            elementId: 'size',
            values: size ? size.split('') : [],
            onNumPadClick: sizeOnChange,
          },
        },
        {
          key: DrainConstants.SET_SIZE_UNIT,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: sizeUnitHiddenFlag,
          props: {
            label: t('drain.sizeUnit'),
            dataTestId: 'sizeUnit',
            options: sizeUnitOptions,
            action: sizeUnitOnChange,
            listHeader: t('drain.sizeUnit'),
            selectedOption: sizeUnit,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: DrainConstants.SET_DRAIN_SUCTION,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: drainSuctionHiddenFlag,
          props: {
            label: t('drain.drainSuction'),
            dataTestId: 'drainSuction',
            options: drainSuctionOptions,
            action: drainSuctionOnChange,
            listHeader: t('drain.drainSuction'),
            selectedOption: drainSuction,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: DrainConstants.SET_CHEST_SUCTION,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: chestSuctionHiddenFlag,
          props: {
            label: t('drain.chestSuction'),
            dataTestId: 'chestSuction',
            options: chestSuctionOptions,
            action: chestSuctionOnChange,
            listHeader: t('drain.chestSuction'),
            selectedOption: chestSuction,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: DrainConstants.SET_FOLEY_COLOR,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: foleyColorHiddenFlag,
          props: {
            label: t('drain.foleyColor'),
            dataTestId: 'foleyColor',
            options: foleyColorOptions,
            action: foleyColorOnChange,
            listHeader: t('drain.foleyColor'),
            selectedOption: foleyColor,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: DrainConstants.SET_CSF_COLOR,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: csfColorHiddenFlag,
          props: {
            label: t('drain.csfColor'),
            dataTestId: 'csfColor',
            options: csfColorOptions,
            action: csfColorOnChange,
            listHeader: t('drain.csfColor'),
            selectedOption: csfColor,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: DrainConstants.SET_SIDE,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: sideHiddenFlag,
          props: {
            label: t('drain.side'),
            dataTestId: 'side',
            options: sideOptions,
            action: sideOnChange,
            listHeader: t('drain.side'),
            selectedOption: side,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: DrainConstants.SET_LOCATION,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: true,
          props: {
            label: t('drain.location'),
            dataTestID: 'location',
            options: locationOptions,
            action: locationOnChange,
            listHeader: t('drain.location'),
            selectedOption: location,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: DrainConstants.SET_INTERCOSTAL_SPACE,
          type: ProcedureConstants.SINGLE_SELECT,
          hidden: true,
          props: {
            label: t('drain.intercostalSpace'),
            dataTestID: 'intercostalSpace',
            options: filteredIntercostalSpaceOptions,
            action: intercostalSpaceOnChange,
            listHeader: t('drain.intercostalSpace'),
            selectedOption: intercostalSpace,
          },
        },
      ],
    },
    {
      key: randomNumber().toString(),
      inputs: [
        {
          key: DrainConstants.SET_DEPTH,
          type: ProcedureConstants.NUMBER_PAD,
          hidden: depthHiddenFlag,
          props: {
            label: t('drain.depth'),
            dataTestId: 'depth',
            elementId: 'depth',
            onNumPadClick: depthOnChange,
            values: depth ? depth.split('') : [],
          },
        },
      ],
    },
  ]

  const setActionDefault = () => {
    const actionDefault = actionOptions.find((option) => option.id === drainDefaults?.defaultAction)

    if (actionDefault) {
      dispatch(setAction(actionDefault))
      return
    }

    dispatch(setUpdateInputRows(true))
  }

  const setFieldDefaults = () => {
    const fieldDefaults = drainDefaults?.defaults.filter((field) => String(field.actionId) === String(action?.id))

    fieldDefaults?.forEach((fieldDefault) => {
      const fieldName = fieldDefault.fieldName
      const defaultValue = fieldDefault.defaultValue

      // Size
      if (fieldName === 'field1') {
        dispatch(setSize(defaultValue || ''))
      }

      // Suction
      else if (fieldName === 'field2') {
        const suctionOption = drainSuctionOptions.find(
          (option) => option.name?.toLowerCase() === defaultValue?.toString().toLowerCase()
        )
        dispatch(setDrainSuction(suctionOption || null))
      }

      // Side
      else if (fieldName === 'field4') {
        const sideOption = sideOptions.find((option) => option.name === defaultValue)
        dispatch(setSide(sideOption || null))
      }

      // Size Unit
      else if (fieldName === 'field5') {
        const sizeUnitOption = sizeUnitOptions.find((option) => option.name === defaultValue)
        dispatch(setSizeUnit(sizeUnitOption || null))
      }
    })

    dispatch(setUpdateInputRows(true))
  }

  useIonViewWillEnter(() => {
    const ageRange = getAgeRangeGroupId(dOB, age, ageUnit)
    const defaultsForAgeRange = getDefaultsForAgeRange(defaults, 'Drain', ageRange)

    if (defaultsForAgeRange) dispatch(setDrainDefaults(defaultsForAgeRange))
    else dispatch(setUpdateInputRows(true))
  }, [defaults, dOB, age, ageUnit])

  useEffect(() => {
    if (!isFirstVisit || !drainDefaults || action) return

    setActionDefault()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drainDefaults])

  useEffect(() => {
    if (!isFirstVisit || !drainDefaults || !action) return

    setFieldDefaults()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, drainDefaults])

  useIonViewDidLeave(() => {
    dispatch(setIsFirstVisit(false))
  })

  if (updateInputRows) {
    dispatch(setInputRows(inputRows))
    dispatch(setUpdateInputRows(false))
  }

  return (
    <IonPage id='DrainPage'>
      <PCRHeader />
      <IonHeader>
        <IonToolbar className='safe-area-space' color='light'>
          <IonButtons slot='start'>
            <IonButton data-testid='Back' fill='clear' onClick={onBackClick} color='primary-alt'>
              <IonIcon icon={chevronBackOutline} />
              {t('common.buttons.Back')}
            </IonButton>
          </IonButtons>
          <IonButtons slot='end'>
            <IonButton data-testid='Save' fill='clear' onClick={onSaveClick} color='primary-alt'>
              {t('common.buttons.Save')}
            </IonButton>
          </IonButtons>
          <IonTitle>Drain</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen color='light'>
        <Procedure
          type={'drain'}
          procedureDate={date}
          procedureTime={time}
          assessedBy={assessedBy}
          successfulAttempt={successfulAttempt}
          rolePerforming={rolePerforming}
          rolePerformingPNNV={rolePerformingPNNV}
          procedureComplications={procedureComplications}
          response={response}
          responsePNNV={responsePNNV}
          authorization={authorization}
          comments={comments}
          attempts={attempts}
          pta={pta}
          isEkgImport={isEkgImport}
          procedurePerformedByAnotherUnit={procedurePerformedByAnotherUnit}
          procedurePerformedByAnotherUnitPNNV={procedurePerformedByAnotherUnitPNNV}
        />
      </IonContent>
    </IonPage>
  )
}

export default Drain
